import { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { ModalDockControls } from './ModalDockControls';
import {
  MODAL_DRAG_HANDLE_ATTR,
  MOVED_SCRIM_COLOR,
  SCRIM_COLOR,
  useBodyScrollLock,
  useModalPlacement,
  useWheelGuard,
} from './useModalPlacement';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  width?: string;
  showCloseButton?: boolean;
  className?: string;
  showBackdrop?: boolean;
  /** Overrides the content wrapper padding (defaults to p-6 per Figma). */
  contentClassName?: string;
  /**
   * Action row pinned below the content (hairline-topped, right-aligned).
   * Content scrolls independently when the modal hits the viewport cap, so
   * footer actions stay reachable on short screens.
   */
  footer?: React.ReactNode;
  /**
   * MPN-24: the modal can be dragged by its header and docked to either
   * viewport edge so the page behind stays readable. On by default; a modal
   * with no header (no title, no close button) has nothing to grab unless its
   * own header carries `data-modal-drag-handle`. Set false to pin it centred.
   */
  movable?: boolean;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  width = 'max-w-md',
  showCloseButton = true,
  className = '',
  showBackdrop = true,
  contentClassName,
  footer,
  movable = true,
}: ModalProps) {
  const titleId = useId();
  const {
    panelRef,
    placement,
    moved,
    dragging,
    panelStyle,
    panelHandlers,
    dock,
  } = useModalPlacement({ isOpen, memoryKey: title, enabled: movable });

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Centred: page scroll locked (as always). Moved aside: the wheel over the
  // scrim scrolls the page behind, but never through the panel itself.
  useBodyScrollLock(isOpen && !moved);
  useWheelGuard(panelRef, isOpen && moved);

  if (!isOpen) return null;

  const hasHeader = Boolean(title || showCloseButton);

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop — swallows clicks on the page in every state. Closes the
          modal only while centred: once the operator has moved the form aside
          to read the page, a click on the page must not throw the form away. */}
      {showBackdrop && (
        <div
          className="fixed inset-0 transition-colors duration-150"
          style={{ backgroundColor: moved ? MOVED_SCRIM_COLOR : SCRIM_COLOR }}
          onClick={moved ? undefined : onClose}
          aria-hidden="true"
          data-modal-scrim=""
        />
      )}

      {/* Modal — Figma "header pop up" family: rounded-8, Drop Pop Up shadow.
          Capped to the viewport so tall content scrolls instead of clipping
          the header/footer off-screen. The translate offset comes from
          useModalPlacement; docking animates, dragging tracks the pointer. */}
      <div
        ref={panelRef}
        {...panelHandlers}
        style={panelStyle}
        className={`relative flex max-h-[calc(100dvh-2rem)] flex-col bg-white rounded-lg shadow-[0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_6px_16px_0px_rgba(0,0,0,0.08),0px_9px_28px_8px_rgba(0,0,0,0.05)] ${width} w-full mx-4 ${
          dragging
            ? 'select-none'
            : 'transition-transform duration-150 ease-out'
        } ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        data-modal-moved={moved ? '' : undefined}
      >
        {/* Header — the drag handle */}
        {hasHeader && (
          <div
            {...(movable ? { [MODAL_DRAG_HANDLE_ATTR]: '' } : {})}
            className={cn(
              'flex shrink-0 items-center justify-between gap-3 px-6 py-4 border-b border-neutral-100',
              movable && 'touch-none select-none',
              movable && (dragging ? 'cursor-grabbing' : 'cursor-grab')
            )}
          >
            {title && (
              <h2
                id={titleId}
                className="font-roboto text-[18px] font-semibold leading-[28px] text-neutral-n800"
              >
                {title}
              </h2>
            )}
            {(movable || showCloseButton) && (
              <div className="ml-auto flex shrink-0 items-center gap-1">
                {movable && (
                  <ModalDockControls placement={placement} onDock={dock} />
                )}
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="rounded-[6px] p-1 text-neutral-n500 hover:text-neutral-n800 transition-colors"
                    aria-label="Close modal"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Content — scrolls when the modal is height-capped */}
        <div className={cn('overflow-y-auto p-6', contentClassName)}>
          {children}
        </div>

        {/* Footer — pinned below the scroll area */}
        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-neutral-4 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
